'use client'

import { useState } from 'react'
import { signIn } from 'next-auth/react'
import { useRouter, useSearchParams } from 'next/navigation'
import { SeericButton } from '@/app/components/SeericButton/SeericButton'
import { Eye, EyeOff, Lock, User } from 'lucide-react'
import AnimatedLettersBG from '../components/animations/AnimatedLettersBG'
import { CookieGuard } from '@/app/components/CookieGuard/CookieGuard'
import { SITE_NAME_UPPER } from '@/constants/site'



export default function SignInPage() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const callbackUrl = searchParams ? searchParams.get('callbackUrl') || '/' : '/'
  const error = searchParams ? searchParams.get('error') : null

  const [formData, setFormData] = useState({ login: '', password: '' })
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsLoading(true)
    setErrorMessage('')

    try {
      const result = await signIn('credentials', {
        login: formData.login,
        password: formData.password,
        redirect: false
      })
      if (result?.error) {
        console.log('Error from signIn:', result.error);
        setErrorMessage(result.error)
      } else {
        router.push(callbackUrl)
        router.refresh()
      }
    } catch {
      console.error('Unexpected error:', error);
      setErrorMessage('Une erreur est survenue lors de la connexion')
    } finally {
      setIsLoading(false)
    }
  }

  const getErrorMessage = (error: string | null) => {
    switch (error) {
      case 'CredentialsSignin':
        return 'Email ou mot de passe incorrect'
      case 'Compte inactif':
        return 'Votre compte est inactif, veuillez contacter l’administrateur.'
      case 'OAuthSignin':
        return 'Erreur lors de la connexion OAuth'
      default:
        return null
    }
  }

  const urlError = getErrorMessage(error)

  return (
    <CookieGuard>
      <div className="relative min-h-screen flex flex-col items-center justify-center">

        <div className="fixed inset-0 w-full h-full pointer-events-none z-0">
          <img
            src="/images/image.png"
            alt=""
            className="w-full h-full object-cover"
            style={{ opacity: 0.5 }}
            draggable={false}
          />
        </div>

        {/* Contenu principal */}
        <div className="relative min-h-screen flex flex-col items-center p-4">
          {/* Animation en haut */}
          <div className="relative lg:absolute lg:top-0 lg:left-1/2 lg:transform lg:-translate-x-1/2 z-20 w-full flex justify-center pointer-events-none mb-4 lg:mb-0">
            <AnimatedLettersBG />
          </div>

          <div className="max-w-md w-full rounded-xl shadow-xl overflow-hidden z-10 relative lg:mt-20">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 sm:px-6 py-6 sm:py-8 text-center">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2">
                Connexion
              </h1>
              <p className="text-base sm:text-lg lg:text-xl">Accédez à votre espace personnel</p>
            </div>

            <div className="p-4 sm:p-6 bg-white/70 text-gray-800 shadow">
              {(urlError || errorMessage) && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 text-base sm:text-lg">
                    {urlError || errorMessage}
                  </p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
                {/* Login */}
                <div>
                  <label htmlFor="login" className="block text-base sm:text-lg lg:text-xl font-medium mb-2">
                    Login
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 sm:h-6 sm:w-6 text-gray-400" />
                    </div>
                    <input
                      id="login"
                      name="login"
                      type="text"
                      value={formData.login}
                      required
                      onChange={handleChange}
                      className="block w-full pl-10 sm:pl-12 pr-3 py-2.5 sm:py-3 text-base sm:text-lg border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="DupontId"
                    />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <label htmlFor="password" className="block text-base sm:text-lg lg:text-xl font-medium mb-2">
                    Mot de passe
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 sm:h-6 sm:w-6 text-gray-400" />
                    </div>
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={formData.password}
                      onChange={handleChange}
                      className="block w-full pl-10 sm:pl-12 pr-10 sm:pr-12 py-2.5 sm:py-3 text-base sm:text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5 sm:h-6 sm:w-6 text-gray-400 hover:text-gray-600" />
                      ) : (
                        <Eye className="h-5 w-5 sm:h-6 sm:w-6 text-gray-400 hover:text-gray-600" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Submit Button */}
                <SeericButton
                  type="submit"
                  disabled={isLoading}
                  variant='secondary'
                  className="w-full h-12 sm:h-14 text-base sm:text-lg lg:text-xl"
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 sm:h-6 sm:w-6 border-b-2 border-white mr-2"></div>
                      Connexion...
                    </div>
                  ) : (
                    'Se connecter'
                  )}
                </SeericButton>
              </form>

              {/* Footer */}
              <div className="mt-6 text-center">
                <p className="text-sm sm:text-base lg:text-lg px-3 py-2 rounded"
                  style={{
                    display: 'inline-block',
                    textShadow: '0 1px 6px rgba(0,0,0,0.15)',
                    color: 'black'
                  }}
                >
                  Attention, nous vous rappelons que vos login et mot de passe sont {`personnels, ${SITE_NAME_UPPER} `}
                  ne pourra être tenue pour responsable de l'utilisation frauduleuse de ces derniers
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </CookieGuard>
  )
}
